import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';
import { THIRD_PARTY_APPS, ZAPIER_CONFIG } from 'constants/constants';
import CustomImage from 'common/CustomImage';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess(true);

const SharePointIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 48 48" 
    width="24px" 
    height="24px"
    style={{ display: 'block' }}
  >
    <path fill="#1a237e" d="M29.5,33.3v-2c4,0,7.3-3.3,7.3-7.3c0-4-3.3-7.3-7.3-7.3v-2c5.1,0,9.3,4.2,9.3,9.3 S34.7,33.3,29.5,33.3z"/>
    <path fill="#0d47a1" d="M41 24c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4S41 21.8 41 24zM32 16c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4S32 13.8 32 16zM32 32c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4S32 29.8 32 32z"/>
    <path fill="#1e88e5" d="M27,42L6,38V10l21-4V42z"/>
    <path fill="#fff" d="M20.5,29.7c-0.3,0.6-0.7,1-1.3,1.3s-1.1,0.6-1.8,0.7C16.7,31.9,16,32,15.2,32 c-0.8,0-1.6-0.1-2.3-0.2c-0.7-0.1-1.3-0.3-1.9-0.6V28c0.6,0.5,1.2,0.9,1.9,1.1s1.4,0.4,2.1,0.4c0.4,0,0.8,0,1.1-0.1 c0.3-0.1,0.6-0.2,0.8-0.3c0.2-0.1,0.4-0.3,0.5-0.5c0.1-0.2,0.2-0.4,0.2-0.6c0-0.3-0.1-0.5-0.2-0.8c-0.2-0.2-0.4-0.4-0.7-0.6 c-0.3-0.2-0.6-0.4-1-0.5c-0.4-0.2-0.8-0.4-1.3-0.5c-1.1-0.5-2-1-2.5-1.7c-0.6-0.7-0.8-1.5-0.8-2.5c0-0.8,0.2-1.4,0.5-1.9 s0.7-1,1.2-1.3c0.5-0.4,1.1-0.6,1.8-0.8c0.7-0.2,1.4-0.3,2.2-0.3c0.8,0,1.4,0,2,0.1c0.6,0.1,1.1,0.2,1.6,0.4v3 c-0.2-0.2-0.5-0.3-0.8-0.4c-0.3-0.1-0.6-0.2-0.9-0.3c-0.3-0.1-0.6-0.1-0.9-0.2s-0.6-0.1-0.9-0.1c-0.4,0-0.7,0-1,0.1 c-0.3,0.1-0.6,0.2-0.8,0.3c-0.2,0.1-0.4,0.3-0.5,0.5c-0.1,0.2-0.2,0.4-0.2,0.6c0,0.2,0.1,0.5,0.2,0.7c0.1,0.2,0.3,0.4,0.5,0.5 c0.2,0.2,0.5,0.3,0.9,0.5c0.3,0.2,0.7,0.3,1.1,0.5c0.6,0.2,1.1,0.5,1.6,0.8c0.5,0.3,0.9,0.6,1.2,0.9c0.3,0.3,0.6,0.7,0.8,1.2 c0.2,0.4,0.3,1,0.3,1.5C21,28.5,20.8,29.2,20.5,29.7z"/>
  </svg>
);

const ExportButton = ({ onClick, title }) => (
  <button
    type="button"
    className="btn btn-sm"
    onClick={onClick}
    style={{ 
      padding: '4px 8px',
      fontSize: '12px',
      border: '1px solid #6c757d',
      borderRadius: '4px',
      backgroundColor: 'transparent',
      color: '#000000',
      cursor: 'pointer',
      transition: 'opacity 0.2s ease'
    }}
    onMouseEnter={(e) => {
      e.target.style.opacity = '0.7';
    }}
    onMouseLeave={(e) => {
      e.target.style.opacity = '1';
    }}
    title={title}
  >
    <i className="icon-upload mr-1" style={{ fontSize: '10px', color: '#000000' }} />
    Export
  </button>
);

const ExportStatusBox = ({ icon, name, statusText, showExportButton, onExport, title }) => (
  <div 
    className="px-3 py-2"
    style={{ 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      width: '100%'
    }}
  >
    <div className="d-flex align-items-center justify-content-between">
      <div className="d-flex align-items-center">
        <div className="mr-3" style={{ minWidth: '24px' }}>
          {icon}
        </div>
        <div>
          <div className="text-black small font-weight-bold mb-1">
            {name}
          </div>
          <div className="text-muted small">
            {statusText}
          </div>
        </div>
      </div>
      {showExportButton && (
        <ExportButton onClick={onExport} title={title} />
      )}
    </div>
  </div>
);

const ExportStatusSection = ({ 
  selectedItem, 
  isPollingExportStatus,
  onSharePointExport,
  onZapierExport
}) => {
  const [isSharePointConnected, setIsSharePointConnected] = useState(false);
  const [isZapierConfigured, setIsZapierConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkConnections = async () => {
      try {
        // Check SharePoint connection
        const thirdPartyResponse = await axios.get(THIRD_PARTY_APPS);
        const sharePointApp = thirdPartyResponse.data.data.find(app => 
          app.id === 'sharepoint' && app.token_exits
        );
        setIsSharePointConnected(!!sharePointApp);

        // Check Zapier configuration
        try {
          const zapierResponse = await axios.get(ZAPIER_CONFIG);
          const config = zapierResponse.data.data;
          setIsZapierConfigured(config.export_enabled);
        } catch {
          setIsZapierConfigured(false);
        }
      } catch (error) {
        console.error('Failed to check connections:', error);
        setIsSharePointConnected(false);
        setIsZapierConfigured(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkConnections();
  }, []);

  const getSharePointExportStatus = () => {
    if (!selectedItem.export_statuses || !selectedItem.export_statuses.sharepoint) {
      return null;
    }
    const sharepointStatus = selectedItem.export_statuses.sharepoint;
    return {
      status: sharepointStatus.status,
      exportedAt: sharepointStatus.exported_at,
      error: sharepointStatus.error
    };
  };

  const getZapierExportStatus = () => {
    if (!selectedItem.export_statuses || !selectedItem.export_statuses.zapier) {
      return null;
    }
    const zapierStatus = selectedItem.export_statuses.zapier;
    return {
      status: zapierStatus.status,
      exportedAt: zapierStatus.exported_at,
      error: zapierStatus.error
    };
  };

  const sharePointStatus = getSharePointExportStatus();
  const zapierStatus = getZapierExportStatus();

  // Determine what to show
  const showSharePoint = isSharePointConnected || sharePointStatus || isPollingExportStatus;
  const showZapier = isZapierConfigured || zapierStatus || isPollingExportStatus;

  // Don't render anything if loading or nothing to show
  if (isLoading || (!showSharePoint && !showZapier)) {
    return null;
  }

  const getSharePointStatusText = () => {
    if (sharePointStatus?.status === 'exported' && sharePointStatus?.exportedAt) {
      return `Exported ${moment.tz(sharePointStatus.exportedAt, timeZone).format('MMM Do YYYY, h:mm A')}`;
    }
    if (sharePointStatus?.status === 'exporting') {
      return 'Exporting...';
    }
    if (sharePointStatus?.status === 'export_failed') {
      return `Export failed${sharePointStatus?.error ? `: ${sharePointStatus.error}` : ''}`;
    }
    return 'Not exported';
  };

  const getZapierStatusText = () => {
    const showExporting = isPollingExportStatus && !zapierStatus;
    if (zapierStatus?.status === 'exported' && zapierStatus?.exportedAt) {
      return `Exported ${moment.tz(zapierStatus.exportedAt, timeZone).format('MMM Do YYYY, h:mm A')}`;
    }
    if (zapierStatus?.status === 'exporting' || showExporting) {
      return 'Exporting...';
    }
    if (zapierStatus?.status === 'export_failed') {
      return `Export failed${zapierStatus?.error ? `: ${zapierStatus.error}` : ''}`;
    }
    return 'Not exported';
  };

  const showSharePointExportButton = !sharePointStatus || sharePointStatus?.status !== 'exported';
  const showZapierExportButton = !zapierStatus || 
    (zapierStatus?.status !== 'exported' && 
     zapierStatus?.status !== 'exporting' && 
     !(isPollingExportStatus && !zapierStatus));

  return (
    <div className="form-group">
      <label htmlFor="forContact" className="d-block mb-2">
        EXPORT TO
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {showSharePoint && isSharePointConnected && (
          <ExportStatusBox
            icon={<SharePointIcon />}
            name="Microsoft SharePoint"
            statusText={getSharePointStatusText()}
            showExportButton={showSharePointExportButton}
            onExport={onSharePointExport}
            title="Export to SharePoint"
          />
        )}
        {showZapier && isZapierConfigured && (
          <ExportStatusBox
            icon={
              <CustomImage
                src="zapier.png"
                alt="Zapier"
                style={{ width: '24px', height: '24px' }}
              />
            }
            name="Zapier"
            statusText={getZapierStatusText()}
            showExportButton={showZapierExportButton}
            onExport={onZapierExport}
            title="Export to Zapier"
          />
        )}
      </div>
    </div>
  );
};

export default ExportStatusSection;

